import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import AdminDashboard from './components/AdminDashboard';
import EmployeeDashboard from './components/EmployeeDashboard';
import SplashScreen from './components/SplashScreen';
import CreateEmployeePage from './components/CreateEmployeePage';
import EditEmployeePage from './components/EditEmployeePage';
import SitesManagementPage from './components/SitesManagementPage';
import EmployeeListPage from './components/EmployeeListPage';
import EmployeeHistoryStart from './components/EmployeeHistoryStart';
import EmployeeHistoryEnd from './components/EmployeeHistoryEnd';
import ConfigurationPage from './components/ConfigurationPage';
import EmployeeDashboardBoard from './components/EmployeeDashboardBoard';
import './App.css';
import ErrorBoundary from './components/ErrorBoundary';
import EmployeeAbsenceRequest from './components/EmployeeAbsenceRequest';
import EmployeePayslips from './components/EmployeePayslips';
import EmployeeTimesheet from './components/EmployeeTimesheet';
import EmployeeAbsenceNew from './components/EmployeeAbsenceNew';
import EmployeeAbsenceList from './components/EmployeeAbsenceList';
import AdminAbsenceManagement from './components/AdminAbsenceManagement';
import AdminTimesheetValidation from './components/AdminTimesheetValidation';
import AdminEscalationsPage from './components/AdminEscalationsPage';
import UpdateNotification from './components/UpdateNotification';
import { versionService } from './services/versionService';
import AdminArchive from './components/AdminArchive';

function AppRoutes() {
  const { currentUser, userRole, loading } = useAuth();
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    // Cacher le splash screen après 2 secondes
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // On attend que le contexte soit prêt (userRole non null) pour router
  if (loading || showSplash || (currentUser && !userRole)) {
    return <SplashScreen onFinish={() => setShowSplash(false)} />;
  }

  if (!currentUser) {
    return <LoginPage />;
  }

  if (userRole === 'admin') {
    return (
      <Routes>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/employees" element={<EmployeeListPage />} />
        <Route path="/admin/sites" element={<SitesManagementPage />} />
        <Route path="/admin/create-employee" element={<CreateEmployeePage />} />
        <Route path="/admin/edit-employee/:id" element={<EditEmployeePage />} />
        <Route path="/admin/config" element={<ConfigurationPage />} />
        <Route path="/admin/absences" element={<AdminAbsenceManagement />} />
        <Route path="/admin/timesheet-validation" element={<AdminTimesheetValidation />} />
        <Route path="/admin/escalations" element={<AdminEscalationsPage />} />
        <Route path="/admin/archives" element={<AdminArchive />} />
        <Route path="*" element={<Navigate to="/admin" />} />
      </Routes>
    );
  }

  if (userRole === 'employee') {
    return (
      <Routes>
        <Route path="/" element={<EmployeeDashboard />} />
        <Route path="/dashboard-board" element={<EmployeeDashboardBoard />} />
        <Route path="/history" element={<EmployeeHistoryStart />} />
        <Route path="/history-end" element={<EmployeeHistoryEnd />} />
        <Route path="/absence-request" element={<EmployeeAbsenceRequest />} />
        <Route path="/absence-request/new" element={<EmployeeAbsenceNew />} />
        <Route path="/absence-request/list" element={<EmployeeAbsenceList />} />
        <Route path="/payslips" element={<EmployeePayslips />} />
        <Route path="/timesheet" element={<EmployeeTimesheet />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    );
  }

  // Cas de fallback (ne devrait jamais arriver)
  return <SplashScreen onFinish={() => setShowSplash(false)} />;
}

function App() {
  useEffect(() => {
    // Initialiser le service de version
    versionService.init().catch(error => {
      console.error('Erreur lors de l\'initialisation du service de version:', error);
    });

    // Nettoyage lors du démontage
    return () => {
      versionService.cleanup();
    };
  }, []);

  return (
    <Router>
      <AuthProvider>
        <ErrorBoundary>
          <AppRoutes />
          <UpdateNotification />
        </ErrorBoundary>
      </AuthProvider>
    </Router>
  );
}

export default App; 